import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { api } from "../../lib/api";
import OtpVerify from "../../components/OtpVerify";
import DateInput from "../../components/DateInput";
import { Eye, EyeOff, AtSign, Lock, User, UserPlus, Phone, CalendarDays, X } from "lucide-react";

/* Small labelled field with a leading icon — matches the reference design */
function AuthField({ icon: Icon, label, children }) {
  return (
    <label className="block text-left">
      <span className="flex items-center gap-1.5 text-[13px] font-bold text-ink mb-1.5">
        <Icon size={13} className="text-ink" /> {label}
      </span>
      {children}
    </label>
  );
}

export default function Login() {
  const { login, signup } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "", birthday: "" });
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [forgot, setForgot] = useState(false);   // forgot-password panel
  const [showClaim, setShowClaim] = useState(false); // showroom-enrolled login setup

  const submit = async (e) => {
    e.preventDefault();
    if (tab === "signup" && form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (tab === "signup" && !phoneVerified) {
      toast.error("Please verify your mobile number with the OTP first");
      return;
    }
    if (tab === "signup" && !form.birthday) {
      toast.error("Please select your date of birth");
      return;
    }
    setBusy(true);
    try {
      if (tab === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Welcome, ${u.name}`);
        nav(u.role === "customer" ? "/" : "/staff");
      } else {
        await signup({ name: form.name, email: form.email, phone: form.phone, password: form.password, birthday: form.birthday });
        toast.success("Account created");
        nav("/");
      }
    } catch (e) {
      if (e?.response?.status === 428) {
        setPhoneVerified(false);
        toast.error("Please verify your mobile number with the OTP first");
      } else if (e?.response?.status === 409) {
        toast.error(e?.response?.data?.detail || "This phone number already exists");
      } else {
        toast.error(e?.response?.data?.detail || "Failed");
      }
    } finally { setBusy(false); }
  };

  const isSignup = tab === "signup";

  return (
    <StorefrontLayout>
      <div className="px-4 lg:px-6 py-10 lg:py-16 flex justify-center">
        <div className="auth-card w-full max-w-4xl grid md:grid-cols-[42%_58%] min-h-[560px]">

          {/* ============ Left: decorative art panel with seam tabs ============ */}
          <div className="auth-art hidden md:block">
            {/* layered rotating fans */}
            <div className="auth-fan" style={{ width: 420, height: 420, top: -140, left: -160 }} />
            <div className="auth-fan slow" style={{ width: 360, height: 360, top: 160, left: -60, opacity: 0.8 }} />
            <div className="auth-fan" style={{ width: 380, height: 380, bottom: -150, left: -130, animationDuration: "110s" }} />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/25" />

            {/* Tabs riding the seam */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-10">
              <button
                onClick={() => setTab("signup")}
                className={`auth-tab pl-7 pr-8 py-3 text-sm ${isSignup ? "active" : "inactive"}`}
                data-testid="signup-tab"
              >
                SIGN UP
              </button>
              <button
                onClick={() => setTab("login")}
                className={`auth-tab pl-7 pr-8 py-3 text-sm ${!isSignup ? "active" : "inactive"}`}
                data-testid="login-tab"
              >
                LOGIN
              </button>
            </div>
          </div>

          {/* Mobile tabs (art panel hidden on small screens) */}
          <div className="flex md:hidden bg-maroon-dark">
            {[["signup", "SIGN UP"], ["login", "LOGIN"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`flex-1 py-3 text-sm font-bold tracking-widest transition-colors ${tab === k ? "bg-white text-maroon" : "text-pearl"}`}
                data-testid={`${k}-tab-mobile`}
              >
                {l}
              </button>
            ))}
          </div>

          {/* ============ Right: the form ============ */}
          <div className="bg-white px-6 sm:px-12 lg:px-16 py-10 flex flex-col justify-center">
            <div className="flex flex-col items-center mb-7">
              <div className="auth-badge">
                {isSignup ? <UserPlus size={30} /> : <User size={30} />}
              </div>
              <h1 className="font-display font-extrabold tracking-[0.08em] text-2xl text-ink mt-4">
                {isSignup ? "SIGN UP" : "LOGIN"}
              </h1>
            </div>

            <form onSubmit={submit} className="space-y-4 max-w-sm w-full mx-auto">
              {isSignup && (
                <>
                  <AuthField icon={User} label="Full Name">
                    <input data-testid="signup-name" placeholder="Your full name" className="auth-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </AuthField>
                  <AuthField icon={Phone} label="Phone">
                    <input data-testid="signup-phone" placeholder="10-digit mobile" maxLength={10} inputMode="numeric" className="auth-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} required />
                    {/^[6-9]\d{9}$/.test(form.phone) && (
                      <div className="mt-2">
                        <OtpVerify
                          phone={form.phone}
                          purpose="signup"
                          compact
                          onVerified={() => setPhoneVerified(true)}
                        />
                      </div>
                    )}
                  </AuthField>
                  <AuthField icon={CalendarDays} label="Date of Birth">
                    <DateInput value={form.birthday} onChange={(v) => setForm({ ...form, birthday: v })} data-testid="signup-birthday" />
                  </AuthField>
                </>
              )}

              <AuthField icon={AtSign} label="Email Address">
                <input data-testid="login-email" type="email" placeholder="name@example.com" className="auth-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} required />
              </AuthField>

              <AuthField icon={Lock} label="Password">
                <div className="relative">
                  <input
                    data-testid="login-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="auth-input pr-11"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ash hover:text-maroon transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </AuthField>

              {isSignup && (
                <AuthField icon={Lock} label="Confirm Password">
                  <input
                    data-testid="signup-confirm"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="auth-input"
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    required
                  />
                  {form.confirm && form.confirm !== form.password && (
                    <span className="text-[11px] text-red-600 mt-1 block">Passwords do not match</span>
                  )}
                </AuthField>
              )}

              <div className="pt-2 text-center">
                <button type="submit" disabled={busy} className="auth-submit text-sm" data-testid="login-submit-btn">
                  {busy ? "…" : isSignup ? "SIGN UP" : "LOGIN"}
                </button>
              </div>
              {!isSignup && (
                <button type="button" onClick={() => setForgot(true)}
                  className="text-xs text-maroon underline w-full text-center"
                  data-testid="forgot-password-link">
                  Forgot password?
                </button>
              )}
            </form>

            {forgot && <ForgotPasswordPanel onClose={() => setForgot(false)} />}

            {!isSignup && !forgot && (
              <div className="mt-4">
                {!showClaim ? (
                  <button type="button" onClick={() => setShowClaim(true)} className="text-xs text-maroon underline w-full text-center" data-testid="claim-account-link">
                    Enrolled at our showroom? Set up your login with OTP
                  </button>
                ) : (
                  <ClaimAccountPanel onClose={() => setShowClaim(false)} />
                )}
              </div>
            )}

            <div className="mt-8 text-center text-xs text-ash">
              Staff member? <Link to="/staff/login" className="text-maroon underline" data-testid="staff-login-link">Login here</Link>
            </div>
          </div>
        </div>
      </div>
    </StorefrontLayout>
  );
}


/* ------- Showroom-enrolled customer: verify phone by OTP, then set login ------- */
function ClaimAccountPanel({ onClose }) {
  const { claimAccount } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState(null); // { registered, has_login, has_email, email, name }
  const [otpVerified, setOtpVerified] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const check = async () => {
    if (!/^[6-9]\d{9}$/.test(phone)) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setBusy(true); setStatus(null); setOtpVerified(false);
    try {
      const { data } = await api.post("/api/auth/phone-status", { phone });
      setStatus(data);
      setEmail(data.email || "");
      if (data.has_login) toast.info("This number already has a login — please sign in with your email & password.");
      else if (!data.registered) toast.error("This number isn't registered. Please sign up instead.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not check the number"); }
    finally { setBusy(false); }
  };

  const finish = async () => {
    if (!otpVerified) { toast.error("Verify the OTP first"); return; }
    if (!email.trim()) { toast.error("Enter your email"); return; }
    if ((password || "").length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const u = await claimAccount({ phone, email: email.trim(), password });
      toast.success("Login created — welcome!");
      nav(u.role === "customer" ? "/account" : "/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set up login"); }
    finally { setBusy(false); }
  };

  const canClaim = status?.registered && !status?.has_login;

  return (
    <div className="border border-gold bg-gold/10 p-4 rounded" data-testid="claim-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-maroon">Set up login (showroom customers)</div>
        <button onClick={onClose} className="text-ash hover:text-maroon"><X size={16}/></button>
      </div>
      <p className="text-[11px] text-ash mb-3">If you were added at our store, verify it's you with a one-time code — then choose a password. We only ask for the OTP to confirm it's the same number.</p>
      <div className="flex gap-2">
        <input placeholder="10-digit mobile" maxLength={10} inputMode="numeric" className="auth-input flex-1" value={phone} onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setStatus(null); }} data-testid="claim-phone"/>
        <button type="button" onClick={check} disabled={busy} className="btn-outline text-xs whitespace-nowrap" data-testid="claim-check">Check</button>
      </div>

      {canClaim && (
        <div className="mt-3 space-y-3">
          <OtpVerify phone={phone} purpose="login" compact onVerified={() => setOtpVerified(true)} />
          <input type="email" placeholder="Email (your login ID)" className="auth-input w-full" value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())} disabled={status?.has_email} data-testid="claim-email"/>
          <input type="password" placeholder="Choose a password (min 6)" className="auth-input w-full" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="claim-password"/>
          <button type="button" onClick={finish} disabled={busy || !otpVerified} className="auth-submit text-sm w-full disabled:opacity-50" data-testid="claim-submit">{busy ? "…" : "Create login & continue"}</button>
        </div>
      )}
    </div>
  );
}


/* ---------------- Forgot password (OTP to registered mobile) ---------------- */
function ForgotPasswordPanel({ onClose }) {
  const [phone, setPhone] = useState("");
  const [stage, setStage] = useState("phone");   // phone -> otp
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [devOtp, setDevOtp] = useState("");

  const sendOtp = async () => {
    if (!/^[6-9]\d{9}$/.test(phone)) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/forgot-password", { phone });
      setStage("otp");
      if (data?.dev_mode && data?.otp) {
        setDevOtp(data.otp);
        toast.info("SMS not configured yet — code shown on screen for testing");
      } else {
        toast.success(data?.message || "OTP sent");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the OTP");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/reset-password", { phone, otp, new_password: pw });
      toast.success("Password updated — please log in");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset the password");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-sm w-full mx-auto mt-5 border border-line bg-white/70 p-4" data-testid="forgot-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow text-[10px]">Reset password</span>
        <button type="button" onClick={onClose} className="text-xs text-ash hover:text-maroon">Close</button>
      </div>

      {stage === "phone" ? (
        <>
          <p className="text-[11px] text-ash mb-2">
            Enter your registered mobile number and we'll send you an OTP.
          </p>
          <div className="flex gap-2">
            <input
              className="auth-input flex-1" placeholder="10-digit mobile" maxLength={10} inputMode="numeric"
              value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              data-testid="forgot-phone"
            />
            <button type="button" onClick={sendOtp} disabled={busy} className="btn-maroon text-xs" data-testid="forgot-send">
              {busy ? "Sending…" : "Send OTP"}
            </button>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <div className="text-[11px] text-ash">
            Enter the code sent to <b className="text-maroon">{phone}</b>.
            {devOtp && <span className="ml-1">Test code: <b className="text-maroon font-mono">{devOtp}</b></span>}
          </div>
          <input
            className="auth-input tracking-[0.3em] font-mono" placeholder="------" maxLength={6}
            value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            data-testid="forgot-otp"
          />
          <input type="password" className="auth-input" placeholder="New password (min 6 characters)"
            value={pw} onChange={(e) => setPw(e.target.value)} data-testid="forgot-pw"/>
          <input type="password" className="auth-input" placeholder="Confirm new password"
            value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="forgot-pw2"/>
          <div className="flex gap-2">
            <button type="button" onClick={reset} disabled={busy || otp.length !== 6}
              className="btn-maroon text-xs flex-1 justify-center disabled:opacity-50" data-testid="forgot-reset">
              {busy ? "Updating…" : "Reset Password"}
            </button>
            <button type="button" onClick={sendOtp} disabled={busy} className="text-xs text-maroon underline">Resend</button>
          </div>
        </div>
      )}
    </div>
  );
}
